/**
 * Wishlist header count (Smart Wishlist app proxy API) and removal of injected span.topbadge.
 */

const WISHLIST_COOKIE = 'wishlist_id=';

const SW_WISHLIST_ADD_CALLBACK_NAMES = [
  'SWCallbackExpressWishlistProductItemAdded',
  'SWCallbackExpressWishlistCollectionItemAdded',
  'SWCallbackCustomerWishlistProductItemAdded',
  'SWCallbackCustomerWishlistCollectionItemAdded',
];

const SW_WISHLIST_REMOVE_CALLBACK_NAMES = [
  'SWCallbackExpressWishlistProductItemRemoved',
  'SWCallbackExpressWishlistCollectionItemRemoved',
  'SWCallbackCustomerWishlistProductItemRemoved',
  'SWCallbackCustomerWishlistCollectionItemRemoved',
];

const SW_WISHLIST_UI_CALLBACK_NAMES = ['SWCallbackWishlistButtonLoaded', 'WishlistLoadedCallback'];

let wishlistCountRefreshTimer = null;
let wishlistFetchHookInstalled = false;
let wishlistCountInitialized = false;
let lastKnownWishlistCount = null;
/** @type {Promise<void> | null} */
let wishlistCountInFlight = null;
let wishlistCountQueued = false;

const getWishlistIdFromCookie = () => {
  const cookies = decodeURIComponent(document.cookie).split(';');

  for (const raw of cookies) {
    const cookie = raw.trim();

    if (cookie.indexOf(WISHLIST_COOKIE) === 0) {
      return cookie.substring(WISHLIST_COOKIE.length);
    }
  }

  return '';
};

const buildWishlistApiUrl = () => {
  let param = '';

  if (typeof window.SWGetCustomerWishlistId === 'function') {
    const cid = window.SWGetCustomerWishlistId();

    if (cid && Number(cid) !== 0) {
      param = `customerid=${encodeURIComponent(String(cid))}`;
    }
  }

  if (!param && typeof window.SWGetExpressWishlistId === 'function') {
    const wid = window.SWGetExpressWishlistId();

    if (wid) {
      param = `wishlistid=${encodeURIComponent(String(wid))}`;
    }
  }

  if (!param) {
    const fromCookie = getWishlistIdFromCookie();

    if (fromCookie) {
      param = `wishlistid=${encodeURIComponent(fromCookie)}`;
    }
  }

  if (!param) {
    return '';
  }

  const cacheBust = `_=${Date.now()}`;

  return `/a/wishlist?type=api&${param}&version=1&${cacheBust}`;
};

const WISHLIST_COUNT_PLACEHOLDER = /\[\[count\]\]/g;

const applyWishlistDrawerCount = (count) => {
  const drawerLink = document.querySelector('.js-wishlist-menu-drawer-link');

  if (!drawerLink) {
    return;
  }

  const suffixWrap = drawerLink.querySelector('.menu-drawer__wishlist-suffix');
  const suffixEl = drawerLink.querySelector('.js-wishlist-drawer-suffix');

  if (!suffixEl) {
    return;
  }

  const one = drawerLink.dataset.wishlistDrawerSuffixOne || '';
  const other = drawerLink.dataset.wishlistDrawerSuffixOther || '';
  const tpl = count === 1 ? one : other;

  suffixEl.textContent = tpl.replace(WISHLIST_COUNT_PLACEHOLDER, String(count));

  if (suffixWrap) {
    suffixWrap.classList.remove('menu-drawer__wishlist-suffix--pending');
  }

  const ariaOne = drawerLink.dataset.wishlistDrawerAriaOne || '';
  const ariaOther = drawerLink.dataset.wishlistDrawerAriaOther || '';
  const ariaTpl = count === 1 ? ariaOne : ariaOther;

  drawerLink.setAttribute('aria-label', ariaTpl.replace(WISHLIST_COUNT_PLACEHOLDER, String(count)));
};

const buildWishlistScreenReaderText = (count, link) => {
  if (count <= 0 || !link?.dataset?.wishlistSr) {
    return '';
  }

  return link.dataset.wishlistSr.replace(WISHLIST_COUNT_PLACEHOLDER, String(count));
};

const setWishlistCounts = (count) => {
  const indicator = document.querySelector('.js-theme-wishlist-header-badge');
  const countEl = indicator?.querySelector('.js-wishlist-count');
  const srEl = indicator?.querySelector('.js-wishlist-sr');
  const link = indicator?.closest('a');

  if (countEl) {
    countEl.textContent = count < 100 ? String(count) : '';
  }

  if (srEl) {
    srEl.textContent = buildWishlistScreenReaderText(count, link);
  }

  applyWishlistDrawerCount(count);
  lastKnownWishlistCount = count;
  wishlistCountInitialized = true;
};

const bumpWishlistCountOptimistic = (delta) => {
  const base = lastKnownWishlistCount ?? 0;

  setWishlistCounts(Math.max(0, base + delta));
};

const scheduleWishlistHeaderCountRefresh = (delayMs = 250) => {
  if (wishlistCountRefreshTimer !== null) {
    window.clearTimeout(wishlistCountRefreshTimer);
  }

  wishlistCountRefreshTimer = window.setTimeout(() => {
    wishlistCountRefreshTimer = null;
    pruneSmartWishlistTopbadges();
    updateWishlistHeaderCount();
  }, delayMs);
};

const updateWishlistHeaderCount = () => {
  if (wishlistCountInFlight) {
    wishlistCountQueued = true;
    return wishlistCountInFlight;
  }

  const url = buildWishlistApiUrl();

  if (!url) {
    if (!wishlistCountInitialized) {
      setWishlistCounts(0);
    }

    return Promise.resolve();
  }

  wishlistCountInFlight = fetch(url, { method: 'GET', cache: 'no-cache' })
    .then((response) => response.json())
    .then((data) => {
      const count = parseInt(data.count, 10);

      if (Number.isFinite(count) && count >= 0) {
        setWishlistCounts(count);
      } else if (!wishlistCountInitialized) {
        setWishlistCounts(0);
      }
    })
    .catch(() => {
      if (!wishlistCountInitialized) {
        setWishlistCounts(0);
      }
    })
    .finally(() => {
      wishlistCountInFlight = null;

      if (wishlistCountQueued) {
        wishlistCountQueued = false;
        scheduleWishlistHeaderCountRefresh(100);
      }
    });

  return wishlistCountInFlight;
};

const scheduleMobileDockWishlistBurst = () => {
  if (!window.matchMedia('(width <= 74.99em)').matches) {
    return;
  }

  const menuBtn = document.getElementById('DockMenuTrigger');

  if (!menuBtn) {
    return;
  }

  const tpl = document.getElementById('WishlistDockBurstHeart');

  if (!tpl?.content) {
    return;
  }

  const existing = menuBtn.querySelector('.mobile-dock__wishlist-burst');

  if (existing) {
    existing.remove();
  }

  const wrap = document.createElement('span');
  wrap.className = 'mobile-dock__wishlist-burst';
  wrap.setAttribute('aria-hidden', 'true');
  wrap.appendChild(tpl.content.cloneNode(true));
  menuBtn.appendChild(wrap);

  requestAnimationFrame(() => {
    wrap.classList.add('mobile-dock__wishlist-burst--animate');
  });

  window.setTimeout(() => {
    wrap.remove();
  }, 950);
};

const WISHLIST_MOUNT_SELECTOR = '.smartwishlist, #smartwishlist';

let wishlistLucideObserverInstalled = false;
let wishlistDecorateTimer = null;
let wishlistDecorating = false;
const observedWishlistMounts = new WeakSet();

const removeWishlistPlaceholders = (root = document) => {
  const scope = root?.querySelector ? root : document;

  for (const mount of scope.querySelectorAll(WISHLIST_MOUNT_SELECTOR)) {
    if (!mount.querySelector('.wishlist-ui-empty, .wishlist-ui-full')) {
      continue;
    }

    for (const placeholder of mount.querySelectorAll('.wishlist-ui-placeholder')) {
      placeholder.remove();
    }
  }
};

const decorateWishlistLucideIcons = (root = document) => {
  if (wishlistDecorating) {
    return;
  }

  const tpl = document.getElementById('WishlistLucideIcons');

  if (!tpl?.content) {
    return;
  }

  const emptySource = tpl.content.querySelector('.wishlist-lucide-empty .icon-heart');
  const fullSource = tpl.content.querySelector('.wishlist-lucide-full .icon-heart');

  if (!emptySource || !fullSource) {
    return;
  }

  const scope = root?.querySelector ? root : document;
  wishlistDecorating = true;

  try {
    for (const el of scope.querySelectorAll('.wishlist-ui-empty, .wishlist-ui-full')) {
      const isFull = el.classList.contains('wishlist-ui-full');
      const icon = el.querySelector('.icon-heart');
      const hasFilled = icon?.classList.contains('icon-heart--filled');

      if (icon && hasFilled === isFull) {
        continue;
      }

      el.replaceChildren((isFull ? fullSource : emptySource).cloneNode(true));
    }

    removeWishlistPlaceholders(scope);
  } finally {
    wishlistDecorating = false;
  }
};

const scheduleWishlistLucideDecorate = (root = document, delayMs = 100) => {
  if (wishlistDecorateTimer !== null) {
    window.clearTimeout(wishlistDecorateTimer);
  }

  wishlistDecorateTimer = window.setTimeout(() => {
    wishlistDecorateTimer = null;
    decorateWishlistLucideIcons(root);
  }, delayMs);
};

const observeWishlistMount = (mount) => {
  if (observedWishlistMounts.has(mount)) {
    scheduleWishlistLucideDecorate(mount);
    return;
  }

  observedWishlistMounts.add(mount);

  const mo = new MutationObserver(() => {
    if (wishlistDecorating) {
      return;
    }

    scheduleWishlistLucideDecorate(mount);
  });

  mo.observe(mount, {
    childList: true,
    subtree: true,
  });

  scheduleWishlistLucideDecorate(mount);
};

const setupWishlistLucideDecorator = () => {
  scheduleWishlistLucideDecorate(document);

  if (wishlistLucideObserverInstalled) {
    return;
  }

  wishlistLucideObserverInstalled = true;

  for (const mount of document.querySelectorAll(WISHLIST_MOUNT_SELECTOR)) {
    observeWishlistMount(mount);
  }

  const rootMo = new MutationObserver((mutations) => {
    if (wishlistDecorating) {
      return;
    }

    let foundMount = false;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) {
          continue;
        }

        if (node.matches?.(WISHLIST_MOUNT_SELECTOR)) {
          observeWishlistMount(node);
          foundMount = true;
          continue;
        }

        for (const mount of node.querySelectorAll?.(WISHLIST_MOUNT_SELECTOR) || []) {
          observeWishlistMount(mount);
          foundMount = true;
        }
      }
    }

    if (foundMount) {
      scheduleWishlistLucideDecorate(document);
    }
  });

  rootMo.observe(document.body, { childList: true, subtree: true });
};

/** UI-only refresh after buttons mount — does not hit the wishlist count API. */
const onSmartWishlistUiReady = () => {
  pruneSmartWishlistTopbadges();
  scheduleWishlistLucideDecorate(document, 150);
};

/** Count-changing mutations (add/remove) — one debounced API refresh. */
const onSmartWishlistMutated = () => {
  pruneSmartWishlistTopbadges();
  scheduleWishlistLucideDecorate(document, 150);
  scheduleWishlistHeaderCountRefresh(300);
};

const onSmartWishlistAdded = () => {
  scheduleMobileDockWishlistBurst();
  bumpWishlistCountOptimistic(1);
  onSmartWishlistMutated();
};

const registerSmartWishlistWindowCallbacks = () => {
  for (const name of SW_WISHLIST_ADD_CALLBACK_NAMES) {
    window[name] = onSmartWishlistAdded;
  }

  for (const name of SW_WISHLIST_REMOVE_CALLBACK_NAMES) {
    window[name] = onSmartWishlistMutated;
  }

  for (const name of SW_WISHLIST_UI_CALLBACK_NAMES) {
    window[name] = onSmartWishlistUiReady;
  }
};

/**
 * Re-populate `.smartwishlist` mount points after AJAX DOM swaps (combined listing colour, etc.).
 * @see https://www.webmarked.net/smart-wishlist-callback-functions/
 */
const reloadSmartWishlistButtons = () => {
  const reload = window.ReloadSmartWishlist;

  if (typeof reload === 'function') {
    reload();
    return true;
  }

  return false;
};

const refreshSmartWishlistMounts = (root = document) => {
  const scope = root && root.querySelector ? root : document;
  const hasMount = scope.querySelector('.smartwishlist, #smartwishlist');

  if (!hasMount) {
    return;
  }

  if (reloadSmartWishlistButtons()) {
    pruneSmartWishlistTopbadges();
    scheduleWishlistLucideDecorate(scope);
    scheduleWishlistHeaderCountRefresh();
    return;
  }

  const delays = [200, 600];

  for (const ms of delays) {
    window.setTimeout(() => {
      if (!scope.querySelector('.smartwishlist, #smartwishlist')) {
        return;
      }

      if (reloadSmartWishlistButtons()) {
        pruneSmartWishlistTopbadges();
        scheduleWishlistLucideDecorate(scope);
        scheduleWishlistHeaderCountRefresh();
      }
    }, ms);
  }
};

window.refreshSmartWishlistMounts = refreshSmartWishlistMounts;

const installWishlistFetchCountRefresh = () => {
  if (wishlistFetchHookInstalled) {
    return;
  }

  wishlistFetchHookInstalled = true;

  const origFetch = window.fetch.bind(window);

  window.fetch = function (...args) {
    const result = origFetch.apply(window, args);
    let url = '';

    if (typeof args[0] === 'string') {
      url = args[0];
    } else if (args[0] instanceof Request) {
      url = args[0].url;
    }

    if (url && url.indexOf('/a/wishlist') !== -1 && url.indexOf('type=api') === -1) {
      result.finally(() => {
        scheduleWishlistHeaderCountRefresh();
      });
    }

    return result;
  };
};

const pruneSmartWishlistTopbadges = () => {
  for (const el of document.querySelectorAll(
    '.header__icon--wishlist .topbadge, .menu-drawer__wishlist-item .topbadge',
  )) {
    el.remove();
  }
};

const setupSmartWishlistTopbadgePruner = () => {
  pruneSmartWishlistTopbadges();

  let scheduled = false;

  const schedule = () => {
    if (scheduled) {
      return;
    }

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      pruneSmartWishlistTopbadges();
    });
  };

  const mo = new MutationObserver(schedule);
  const wl = document.querySelector('.header__icon--wishlist');
  const drawer = document.getElementById('MenuDrawer');

  if (wl) {
    mo.observe(wl, { childList: true, subtree: true });
  }

  if (drawer) {
    mo.observe(drawer, { childList: true, subtree: true });
  }
};

const init = () => {
  installWishlistFetchCountRefresh();
  setupSmartWishlistTopbadgePruner();
  setupWishlistLucideDecorator();
  scheduleWishlistHeaderCountRefresh(0);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      pruneSmartWishlistTopbadges();
      scheduleWishlistHeaderCountRefresh(200);
    }
  });

  document.addEventListener('quick-view:loaded', (event) => {
    const content = event.target?.closest?.('#QuickViewContent') || document.getElementById('QuickViewContent');

    if (content) {
      refreshSmartWishlistMounts(content);
    }
  });

  window.addEventListener('pageshow', () => {
    pruneSmartWishlistTopbadges();
    scheduleWishlistHeaderCountRefresh(0);
  });

  window.addEventListener('load', () => {
    pruneSmartWishlistTopbadges();
    scheduleWishlistHeaderCountRefresh(0);
  });
};

registerSmartWishlistWindowCallbacks();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
